/**
 * What a connection can reach (discovery), and what the new-experiment flow
 * keeps about "what to test" — `docs/agent-access-redesign.html` §04.
 */

import type { components } from "@/lib/api-schema";
import type { InputRow } from "./http/input-fields-editor";

type Schemas = components["schemas"];

/** One field a connection of this type takes. Every one is stored encrypted;
 *  `reveal` ones (an id, a header name) are shown back in full. */
export type SettingField = Schemas["SettingFieldOut"];
/** `capabilities`: `discover` (groups → targets), `versions`, `preview` (shows its request). */
export type Connector = Schemas["ConnectorOut"];
export type RequestPreviewInput = Schemas["RequestPreviewIn"];
export type RequestPreview = Schemas["RequestPreviewOut"];
export type RemoteGroup = Schemas["GroupOut"];
/** `name` is the id to pass as `target` (Orchestrator: the agent slug). */
export type RemoteTarget = Schemas["TargetOut"];
export type RemoteVersion = Schemas["workbench__entrypoints__api__schemas__discovery__VersionOut"];

/** "production" (resolved to its number at start) or a version number. */
export const PRODUCTION = "production";

/**
 * What the person has chosen in step 2. A field left `undefined` falls back to
 * a default derived on render (the only group, the prefilled agent); `null`
 * means cleared on purpose.
 */
export interface TargetDraft {
  group?: string | null;
  target?: string | null;
  version: string;
  /** HTTP endpoint agents: the path under the base URL, and what each call takes. */
  path?: string;
  agentName?: string;
  inputs?: InputRow[];
  task?: string;
  schemaText?: string;
}

/** The draft with every default applied, as the steps and payloads see it. */
export interface ResolvedTarget extends TargetDraft {
  group: string | null;
  target: string | null;
  /** The workbench's agent for this target, when one already exists. */
  agentId: string | null;
}

/** The target part of `POST /experiments` and `POST /experiments/try`. */
export type TargetPayload = Pick<
  Schemas["ExperimentCreate"],
  "group" | "target" | "external_ref" | "inputs" | "output_schema" | "task" | "agent_name"
> & { connection_id: string; version: string };
